import React from 'react';

import { CtaButton, CtaButtonProps } from 'components/global/CtaButton';
import {
  BeforeAfterCase,
  type BeforeAfterCaseProps,
} from './components/BeforeAfterCase';
import { desktopOnly } from 'styles/mixins/displayMobileDesktop';
import type { Theme } from '../types';
import { mobileOnly } from 'styles/mixins/displayMobileDesktop';

import {
  BeforeAfterPopContainer,
  Disclaimer,
  ExamplesContainer,
  Header,
  HeaderContent,
  SubheaderContent,
  InnerBox,
  Subheader,
  QuoteSummary,
} from './BeforeAfterPop.style';
import FiveStars from 'components/global/FiveStars';
import { useLocation } from '@gatsbyjs/reach-router';

export interface BeforeAfterPopProps {
  header: string;
  subheader?: string | React.ReactNode;
  persistentScrollBar: boolean;
  examples: BeforeAfterCaseProps[];
  ctaButton?: CtaButtonProps;
  disclaimer?: string;
  theme?: Theme;
  backgroundColor?: string;
}

const moduleName = 'beforeAfterPop';

export const BeforeAfterPop = ({
  header,
  subheader,
  persistentScrollBar,
  examples,
  ctaButton,
  disclaimer,
  backgroundColor,
}: BeforeAfterPopProps) => {
  const { pathname } = useLocation();
  const useCustomSubheader = pathname === '/';
  const customSubheader = (
    <div>
      <FiveStars /> 9k+ 5 star reviews<sup>▵</sup>
    </div>
  );
  const singleExample = examples.length === 1;
  const { summary, quote } = examples[0];

  return (
    <BeforeAfterPopContainer
      data-module={moduleName}
      backgroundColor={backgroundColor}
    >
      <InnerBox singleExample={singleExample}>
        {singleExample && (
          <ExamplesContainer singleExample={singleExample} css={desktopOnly()}>
            {examples.map((example, index) => (
              <BeforeAfterCase key={index} imageOnly={true} {...example} />
            ))}
          </ExamplesContainer>
        )}
        <HeaderContent singleExample={singleExample}>
          {header && <Header dangerouslySetInnerHTML={{ __html: header }} />}
          {singleExample && (
            <ExamplesContainer singleExample={singleExample} css={mobileOnly()}>
              {examples.map((example, index) => (
                <BeforeAfterCase key={index} imageOnly={true} {...example} />
              ))}
            </ExamplesContainer>
          )}
          {!subheader && !useCustomSubheader && ctaButton && (
            <CtaButton css={desktopOnly()} {...ctaButton} />
          )}
          {singleExample && (
            <React.Fragment>
              <QuoteSummary>
                <h2>{summary}</h2>
                {quote && <div>{quote}</div>}
              </QuoteSummary>

              {disclaimer && <Disclaimer>{disclaimer}</Disclaimer>}
            </React.Fragment>
          )}
        </HeaderContent>
        {(subheader || useCustomSubheader) && (
          <SubheaderContent>
            <Subheader>
              {useCustomSubheader ? customSubheader : subheader}
            </Subheader>
            {ctaButton && <CtaButton css={desktopOnly()} {...ctaButton} />}
          </SubheaderContent>
        )}
        {!singleExample && (
          <ExamplesContainer
            persistentScrollBar={persistentScrollBar}
            tabIndex={0}
            role="region"
            aria-label="Before and after examples, horizontally scrollable gallery"
          >
            {examples.map((example, index) => (
              <BeforeAfterCase key={index} {...example} />
            ))}
          </ExamplesContainer>
        )}
        {ctaButton && <CtaButton css={[mobileOnly()]} {...ctaButton} />}
        {!singleExample && disclaimer && <Disclaimer>{disclaimer}</Disclaimer>}
      </InnerBox>
    </BeforeAfterPopContainer>
  );
};
